interface Vehicle {
  assignedDriver: string;
  brand: string;
  color: string;
  driverId?: string;
  fuel_type: string;
  id: string;
  insurance_number: string;
  license: string;
  make: string;
  max_load: string;
  mileage: string;
  model: string;
  plate_number: string;
  status: "active" | "inactive" | "maintenance";
  type: string;
  vehicleType?: string;
  year: string;
}

// Helper to map Maintenance to MaintenanceFormData
import { Edit, Eye, Package, Plus, Trash2, Truck, Users } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

import FleetService from "@/services/FleetService";

import AddDriverModal, { DriverFormData } from "./modals/AddDriverModal";
import AddMaintenanceModal from "./modals/AddMaintenanceModal";
import AddVehicleModal from "./modals/AddVehicleModal";
import DriverViewModal from "./modals/DriverViewModal";
import EditDriverModal from "./modals/EditDriverModal";
import EditMaintenanceModal from "./modals/EditMaintenanceModal";
import VehicleEditModal from "./modals/EditVehicleModal";
import MaintenanceViewModal from "./modals/MaintenanceViewModal";
import VehicleViewModal from "./modals/VehicleViewModal";

// Helper to map Vehicle to VehicleFormData

const FleetManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState("vehicles");
  const location = useLocation();

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const tab = params.get("tab");
      if (tab) setActiveTab(tab);
    } catch (err) {
      // ignore
    }
  }, [location.search]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<DriverFormData[]>([]);
  const [maintenance, setMaintenance] = useState<any[]>([]);

  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [showAddMaintenance, setShowAddMaintenance] = useState(false);
  const [viewVehicle, setViewVehicle] = useState<null | Vehicle>(null);
  const [editVehicle, setEditVehicle] = useState<null | Vehicle>(null);
  const [deleteVehicle, setDeleteVehicle] = useState<null | Vehicle>(null);
  const [isDeletingVehicle, setIsDeletingVehicle] = useState(false);
  const [vehicleStatusFilter, setVehicleStatusFilter] = useState("");
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [driverStatusFilter, setDriverStatusFilter] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [driverView, setDriverView] = useState<"grid" | "table">("table");
  const [editDriver, setEditDriver] = useState<DriverFormData | null>(null);
  const [viewDriver, setViewDriver] = useState<DriverFormData | null>(null);
  const [deleteDriver, setDeleteDriver] = useState<DriverFormData | null>(null);
  const [isDeletingDriver, setIsDeletingDriver] = useState(false);
  const [deleteMaintenance, setDeleteMaintenance] = useState<null | {
    date: string;
    id: string;
    status: string;
    vehicle: string;
  }>(null);
  const [isDeletingMaintenance, setIsDeletingMaintenance] = useState(false);
  // Robust driver deletion using backend
  const handleDeleteDriver = async (driver: DriverFormData) => {
    if (!driver) return;
    setIsDeletingDriver(true);
    try {
      // Use a unique identifier for driver, e.g. email or vehicle (if vehicle is driverId)
      // Adjust this to match your backend's expected driver ID field
      const driverId = driver.id;
      await FleetService.deleteDriver(driverId);
      toast.success("Driver deleted successfully");
      setDrivers((prev) =>
        prev.filter((d) => (d.vehicle || d.email || d.phone) !== driverId),
      );
      setDeleteDriver(null);
    } catch (error) {
      console.error("Error deleting driver:", error);
      toast.error("Failed to delete driver");
    } finally {
      setIsDeletingDriver(false);
    }
  };

  // Robust maintenance deletion using backend
  const handleDeleteMaintenance = async (maintenance: { id: string }) => {
    if (!maintenance?.id) return;
    setIsDeletingMaintenance(true);
    try {
      await FleetService.deleteMaintenance(maintenance.id);
      toast.success("Maintenance record deleted successfully");
      setMaintenance((prev) => prev.filter((m) => m.id !== maintenance.id));
      setDeleteMaintenance(null);
    } catch (error) {
      console.error("Error deleting maintenance:", error);
      toast.error("Failed to delete maintenance record");
    } finally {
      setIsDeletingMaintenance(false);
    }
  };
  const [maintenanceView, setMaintenanceView] = useState<"grid" | "table">(
    "table",
  );
  const [editMaintenance, setEditMaintenance] = useState<null | {
    cost?: number;
    date: string;
    id: string;
    maintenanceType?: string;
    notes?: string;
    odometer?: number;
    serviceProvider?: string;
    status: string;
    vehicle: string;
  }>(null);
  const [viewMaintenance, setViewMaintenance] = useState<null | {
    cost?: number;
    date: string;
    id: string;
    maintenanceType?: string;
    notes?: string;
    odometer?: number;
    serviceProvider?: string;
    status: string;
    vehicle: string;
  }>(null);
  const [maintenanceStatusFilter, setMaintenanceStatusFilter] = useState("");
  const [maintenanceSearch, setMaintenanceSearch] = useState("");
  const [vehicleView, setVehicleView] = useState<"grid" | "table">("table");
  const [isLoading, setIsLoading] = useState(true);

  const handleDeleteVehicle = async (vehicle: Vehicle) => {
    if (!vehicle?.id) return;

    setIsDeletingVehicle(true);
    try {
      await FleetService.deleteVehicle(vehicle.id);
      toast.success("Vehicle deleted successfully");
      setVehicles((prev) => prev.filter((v) => v.id !== vehicle.id));
      setDeleteVehicle(null);
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      toast.error("Failed to delete vehicle");
    } finally {
      setIsDeletingVehicle(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      console.log("Fetching fleet data...");

      // Fetch vehicles
      try {
        const vehiclesRes = await FleetService.getVehicles();

        if (vehiclesRes && Array.isArray(vehiclesRes)) {
          const mappedVehicles = vehiclesRes.map((v: any) => ({
            assignedDriver:
              typeof v.assigned_driver === "object" &&
              v.assigned_driver?.full_name
                ? v.assigned_driver.full_name
                : typeof v.assigned_driver === "string"
                  ? v.assigned_driver
                  : "Unassigned",
            brand: v.brand || v.make || "",
            color: v.color || "",
            driverId:
              v.assigned_driver_id?.toString() ||
              v.driver_id?.toString() ||
              v.driverId?.toString() ||
              "",
            fuel_type: v.fuel_type || v.fuelType || "",
            id: v.id?.toString() || "",
            insurance_number: v.insurance_number || v.insuranceNumber || "",
            license: v.license || v.licensePlate || v.plate_number || "", // <-- Added license property
            make: v.make || v.brand || "",
            max_load: v.max_load || v.maxLoad || "",
            mileage: v.mileage?.toString() || "0",
            model: v.model || "",
            plate_number: v.plate_number || v.plate || v.licensePlate || "",
            registrationDocument:
              v.registration_document || v.registrationDocument || null,
            status: ["active", "inactive", "maintenance"].includes(v.status)
              ? v.status
              : "active",
            type: v.type || v.vehicle_type || v.vehicleType || "",
            vehicleType: v.vehicle_type || v.vehicleType || v.type || "",
            year: v.year?.toString() || "",
          }));
          setVehicles(mappedVehicles);
          console.log("Vehicles set:", mappedVehicles.length);
        } else {
          console.log("No vehicles data or invalid format");
        }
      } catch (err) {
        console.error("Error fetching vehicles:", err);
      }

      // Fetch drivers
      try {
        const driversRes = await FleetService.getDrivers();
        console.log("Drivers response:", driversRes);

        if (driversRes && Array.isArray(driversRes)) {
          const mappedDrivers = driversRes.map((d: any) => ({
            address: d.address || "",
            assigned_vehicle_id:
              d.assigned_vehicle_id?.toString() ||
              d.vehicle_id?.toString() ||
              d.vehicle ||
              "",
            email: d.email || "",
            full_name: d.full_name || d.name || "",
            id: d.id?.toString() || "",
            licenseExpiry: d.license_expiry || "",
            licenseNumber: d.license_number || "",
            phone: d.phone || d.mobile || "",
            status: d.status || "active",
            vehicle:
              d.assigned_vehicle_id?.toString() ||
              d.vehicle_id?.toString() ||
              d.vehicle ||
              "",
          }));
          setDrivers(mappedDrivers);
          console.log("Drivers set:", mappedDrivers.length);
        } else {
          console.log("No drivers data or invalid format");
        }
      } catch (err) {
        console.error("Error fetching drivers:", err);
      }

      // Fetch maintenance
      try {
        const maintenanceRes = await FleetService.getMaintenances();
        console.log("Maintenance response:", maintenanceRes);

        if (maintenanceRes && Array.isArray(maintenanceRes)) {
          const mappedMaintenance = maintenanceRes.map((m: any) => ({
            cost: m.cost || 0,
            date: m.date || m.maintenance_date || "",
            id: m.id?.toString() || "",
            maintenanceType: m.maintenance_type || m.type || "",
            notes: m.notes || "",
            odometer: m.odometer || 0,
            serviceProvider: m.service_provider || m.provider || "",
            status: m.status || "pending",
            vehicle: m.vehicle_id?.toString() || m.vehicle?.toString() || "",
          }));
          setMaintenance(mappedMaintenance);
          console.log("Maintenance set:", mappedMaintenance.length);
        } else {
          console.log("No maintenance data or invalid format");
        }
      } catch (err) {
        console.error("Error fetching maintenance:", err);
      }

      console.log("Fleet data fetch completed");
    } catch (err) {
      console.error("Failed to fetch fleet data", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredVehicles = vehicles
    .filter((v) => !vehicleStatusFilter || v.status === vehicleStatusFilter)
    .filter(
      (v) =>
        !vehicleSearch ||
        v.make.toLowerCase().includes(vehicleSearch.toLowerCase()) ||
        v.model.toLowerCase().includes(vehicleSearch.toLowerCase()) ||
        v.plate_number.toLowerCase().includes(vehicleSearch.toLowerCase()),
    );
  const filteredDrivers = drivers
    .filter((d) => !driverStatusFilter || d.status === driverStatusFilter)
    .filter(
      (d) =>
        !driverSearch ||
        d.full_name.toLowerCase().includes(driverSearch.toLowerCase()) ||
        d.phone.toLowerCase().includes(driverSearch.toLowerCase()),
    );
  const filteredMaintenance = maintenance
    .filter(
      (m) => !maintenanceStatusFilter || m.status === maintenanceStatusFilter,
    )
    .filter(
      (m) =>
        !maintenanceSearch ||
        m.vehicle.toLowerCase().includes(maintenanceSearch.toLowerCase()) ||
        m.status.toLowerCase().includes(maintenanceSearch.toLowerCase()),
    );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "inactive":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
      case "maintenance":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const tabs = [
    { icon: Truck, id: "vehicles", label: "Vehicles" },
    { icon: Users, id: "drivers", label: "Drivers" },
    { icon: Package, id: "maintenance", label: "Maintenance" },
  ];

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Tabs */}
      <div className="mb-3 border-b border-gray-200 dark:border-gray-700 sm:mb-4">
        <nav className="-mb-px flex space-x-4 overflow-x-auto sm:space-x-8">
          {tabs.map((tab) => (
            <button
              className={`flex items-center gap-1.5 border-b-2 px-1 py-2 text-xs font-medium sm:gap-2 sm:text-sm ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "vehicles" && (
        <>
          <AddVehicleModal
            onClose={() => setShowAddVehicle(false)}
            onSubmit={(data) => {
              setShowAddVehicle(false);
              fetchData(); // Refresh the list
            }}
            open={showAddVehicle}
          />
          <VehicleViewModal
            onClose={() => setViewVehicle(null)}
            onDelete={(vehicleId) => {
              setVehicles((prev) => prev.filter((v) => v.id !== vehicleId));
              setViewVehicle(null);
            }}
            onRefresh={fetchData}
            open={!!viewVehicle}
            vehicle={viewVehicle}
          />
          {editVehicle && (
            <VehicleEditModal
              drivers={drivers.map((d) => ({
                id: d.id ? String(d.id) : "",
                name: d.full_name,
              }))}
              initialData={{
                assignedDriver: editVehicle.driverId
                  ? String(editVehicle.driverId)
                  : "",
                brand: editVehicle.brand || "",
                color: editVehicle.color || "",
                fuel_type: editVehicle.fuel_type || "",
                id: editVehicle.id || "",
                insurance_number: editVehicle.insurance_number || "",
                max_load: editVehicle.max_load || "",
                mileage: editVehicle.mileage || "",
                model: editVehicle.model || "",
                plate_number: editVehicle.plate_number || "",
                registrationDocument: undefined,
                status: editVehicle.status,
                type: editVehicle.type || editVehicle.vehicleType || "",
                year: editVehicle.year || "",
              }}
              onClose={() => setEditVehicle(null)}
              onSubmit={async (data) => {
                const payload: {
                  assigned_driver_id: string;
                  brand: string;
                  color: string;
                  fuel_type: string;
                  id: string;
                  insurance_number: string;
                  max_load: string;
                  mileage: number;
                  model: string;
                  plate_number: string;
                  status: "active" | "inactive" | "maintenance";
                  type: string;
                  year: string;
                } = {
                  assigned_driver_id: data.assignedDriver,
                  brand: data.brand,
                  color: data.color,
                  fuel_type: data.fuel_type,
                  id: editVehicle.id,
                  insurance_number: data.insurance_number,
                  max_load: data.max_load,
                  mileage: data.mileage ? parseInt(data.mileage, 10) : 0,
                  model: data.model,
                  plate_number: data.plate_number,
                  status: data.status,
                  type: data.type,
                  year: data.year,
                };
                if (
                  data.registrationDocument &&
                  data.registrationDocument instanceof File
                ) {
                  const fd = new FormData();
                  // Add all payload fields to FormData with proper type handling
                  fd.append("id", payload.id);
                  fd.append("brand", payload.brand);
                  fd.append("color", payload.color);
                  fd.append("fuel_type", payload.fuel_type);
                  fd.append("insurance_number", payload.insurance_number);
                  fd.append("max_load", payload.max_load);
                  fd.append("mileage", payload.mileage.toString());
                  fd.append("model", payload.model);
                  fd.append("plate_number", payload.plate_number);
                  fd.append("status", payload.status);
                  fd.append("type", payload.type);
                  fd.append("year", payload.year);
                  if (payload.assigned_driver_id) {
                    fd.append("assigned_driver_id", payload.assigned_driver_id);
                  }
                  fd.append("registration_document", data.registrationDocument);
                  await FleetService.updateVehicle(editVehicle.id, fd as any);
                } else {
                  await FleetService.updateVehicle(editVehicle.id, payload);
                }
                setEditVehicle(null);
                fetchData();
              }}
              open={true}
            />
          )}
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Fleet Vehicles ({vehicles.length})
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                View:
              </span>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${vehicleView === "grid" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() => setVehicleView("grid")}
              >
                Cards
              </button>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${vehicleView === "table" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() => setVehicleView("table")}
              >
                Table
              </button>
              <button
                className="ml-2 flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:ml-4 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                onClick={() => setShowAddVehicle(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Add Vehicle</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <select
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setVehicleStatusFilter(e.target.value)}
              value={vehicleStatusFilter}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="maintenance">Maintenance</option>
            </select>
            <input
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:flex-1 sm:text-sm"
              onChange={(e) => setVehicleSearch(e.target.value)}
              placeholder="Search vehicles..."
              type="text"
              value={vehicleSearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 sm:text-sm"
              onClick={() => {
                const csv = [
                  [
                    "Year",
                    "Make",
                    "Model",
                    "License Plate",
                    "Status",
                    "Mileage",
                  ],
                  ...filteredVehicles.map((v) => [
                    v.year,
                    v.make,
                    v.model,
                    v.plate_number,
                    v.status,
                    v.mileage,
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "vehicles.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {isLoading ? (
            vehicleView === "table" ? (
              <div className="w-full overflow-x-auto">
                <table className="w-full rounded-lg bg-white text-xs shadow-md dark:bg-gray-800 sm:text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Vehicle
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        License Plate
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Mileage
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                    {[...Array(5)].map((_, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4">
                          <div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                          <div className="mt-1 h-3 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-6 w-16 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    className="w-full rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                    key={i}
                  >
                    <div className="h-5 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="mt-2 h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="mt-2 h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="mt-2 h-4 w-28 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="mt-4 flex gap-2">
                      <div className="h-8 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                      <div className="h-8 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                      <div className="h-8 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <>
              {vehicleView === "table" ? (
                <div className="w-full overflow-x-auto">
                  <table className="w-full rounded-lg bg-white text-xs shadow-md dark:bg-gray-800 sm:text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Vehicle
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          License Plate
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Mileage
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                      {filteredVehicles.map((vehicle) => (
                        <tr key={vehicle.id}>
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {vehicle.status === "maintenance"
                                  ? `${vehicle.brand} ${vehicle.model} ${vehicle.plate_number}`
                                  : `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                            {vehicle.plate_number}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(vehicle.status)}`}
                            >
                              {vehicle.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                            {parseInt(vehicle.mileage).toLocaleString()} km
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                                onClick={() => setViewVehicle(vehicle)}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                                onClick={() => setEditVehicle(vehicle)}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                                onClick={() => setDeleteVehicle(vehicle)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredVehicles.map((vehicle) => (
                    <div
                      className="w-full rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                      key={vehicle.id}
                    >
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {vehicle.status === "maintenance"
                          ? `${vehicle.brand} ${vehicle.model} ${vehicle.plate_number}`
                          : `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      </div>
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        License Plate: {vehicle.plate_number}
                      </div>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(vehicle.status)}`}
                      >
                        {vehicle.status}
                      </span>
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        Mileage: {parseInt(vehicle.mileage).toLocaleString()} km
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                          onClick={() => setViewVehicle(vehicle)}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                          onClick={() => setEditVehicle(vehicle)}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                          onClick={() => setDeleteVehicle(vehicle)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {activeTab === "drivers" && (
        <>
          <AddDriverModal
            onClose={() => setShowAddDriver(false)}
            onSubmit={(data) => {
              setShowAddDriver(false);
              fetchData(); // Refresh the list
            }}
            open={showAddDriver}
          />
          {/* Edit/View Driver Modals */}
          {editDriver && (
            <EditDriverModal
              initialData={{
                address: editDriver.address || "",
                email: editDriver.email || "",
                id: editDriver.id || "",
                licenseExpiry: editDriver.licenseExpiry || "",
                licenseNumber: editDriver.licenseNumber || "",
                name: editDriver.full_name || "",
                phone: editDriver.phone || "",
                rating: 0,
                status: editDriver.status || "active",
                // Ensure vehicle is always a string of the assigned_vehicle_id
                vehicle: editDriver.assigned_vehicle_id
                  ? String(editDriver.assigned_vehicle_id)
                  : "",
              }}
              onClose={() => setEditDriver(null)}
              onSubmit={async (data) => {
                try {
                  const driverId = editDriver.id;
                  await FleetService.updateDriver(driverId, {
                    address: data.address,
                    assigned_vehicle_id: data.vehicle,
                    email: data.email,
                    full_name: data.name,
                    license_expiry: data.licenseExpiry,
                    license_number: data.licenseNumber,
                    phone: data.phone,
                    rating: data.rating,
                    status: data.status,
                  });
                  fetchData(); // Refresh driver list from backend
                  setEditDriver(null);
                } catch (err) {
                  // Optionally show error toast
                }
              }}
              open={true}
            />
          )}
          {viewDriver && (
            <DriverViewModal
              driver={viewDriver}
              onClose={() => setViewDriver(null)}
              vehicles={vehicles}
            />
          )}
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Drivers ({drivers.length})
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                View:
              </span>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${driverView === "grid" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() => setDriverView("grid")}
              >
                Cards
              </button>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${driverView === "table" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() => setDriverView("table")}
              >
                Table
              </button>
              <button
                className="ml-2 flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:ml-4 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                onClick={() => setShowAddDriver(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Add Driver</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <select
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setDriverStatusFilter(e.target.value)}
              value={driverStatusFilter}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <input
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:flex-1 sm:text-sm"
              onChange={(e) => setDriverSearch(e.target.value)}
              placeholder="Search drivers..."
              type="text"
              value={driverSearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 sm:text-sm"
              onClick={() => {
                const csv = [
                  ["Full Name", "Phone", "Status"],
                  ...filteredDrivers.map((d) => [
                    d.full_name,
                    d.phone,
                    d.status,
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "drivers.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {isLoading ? (
            driverView === "table" ? (
              <div className="w-full overflow-x-auto">
                <table className="w-full rounded-lg bg-white text-xs shadow-md dark:bg-gray-800 sm:text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                    {[...Array(5)].map((_, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4">
                          <div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 w-28 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-6 w-16 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    className="w-full rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                    key={i}
                  >
                    <div className="h-5 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="mt-2 h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="mt-2 h-6 w-20 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700"></div>
                    <div className="mt-4 flex gap-2">
                      <div className="h-8 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                      <div className="h-8 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <>
              {driverView === "table" ? (
                <div className="w-full overflow-x-auto">
                  <table className="w-full rounded-lg bg-white text-xs shadow-md dark:bg-gray-800 sm:text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Phone
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                      {filteredDrivers.map((driver, idx) => (
                        <tr key={idx}>
                          <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100">
                            {driver.full_name}
                          </td>
                          <td className="px-6 py-4 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                            {driver.phone}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(driver.status)}`}
                            >
                              {driver.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                                onClick={() => setViewDriver(driver)}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                                onClick={() => setEditDriver(driver)}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                                onClick={() => setDeleteDriver(driver)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredDrivers.map((driver, idx) => (
                    <div
                      className="w-full rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                      key={idx}
                    >
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        {driver.full_name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Phone: {driver.phone}
                      </p>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(driver.status)}`}
                      >
                        {driver.status}
                      </span>
                      <div className="mt-2 flex gap-2">
                        {/* Eye icon removed */}
                        <button
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                          onClick={() => setViewDriver(driver)}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                          onClick={() => setEditDriver(driver)}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                          onClick={() => setDeleteDriver(driver)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {activeTab === "maintenance" && (
        <>
          <AddMaintenanceModal
            onClose={() => setShowAddMaintenance(false)}
            onSubmit={(data) => {
              setShowAddMaintenance(false);
              fetchData(); // Refresh the list
            }}
            open={showAddMaintenance}
            vehicles={vehicles.map((v) => ({
              id: v.id,
              label: `${v.year} ${v.make} ${v.model}`,
            }))}
          />
          {/* Edit/View Maintenance Modals */}
          {editMaintenance && (
            <EditMaintenanceModal
              initialData={{
                cost: editMaintenance.cost ?? 0,
                date: editMaintenance.date,
                maintenanceType: editMaintenance.maintenanceType ?? "",
                notes: editMaintenance.notes ?? "",
                odometer: editMaintenance.odometer ?? 0,
                serviceProvider: editMaintenance.serviceProvider ?? "",
                status: editMaintenance.status as any,
                vehicle: editMaintenance.vehicle,
              }}
              onClose={() => setEditMaintenance(null)}
              onSubmit={(data) => {
                const payload = {
                  cost: data.cost,
                  date: data.date,
                  id: editMaintenance.id,
                  maintenance_type: data.maintenanceType,
                  notes: data.notes,
                  odometer: data.odometer,
                  service_provider: data.serviceProvider,
                  status: data.status,
                  vehicle_id: data.vehicle,
                };
                (async () => {
                  try {
                    await FleetService.updateMaintenance(
                      editMaintenance.id,
                      payload,
                    );
                    toast.success("Maintenance record updated successfully");
                    fetchData();
                  } catch (error) {
                    console.error("Error updating maintenance record:", error);
                    toast.error("Failed to update maintenance record");
                  } finally {
                    setEditMaintenance(null);
                  }
                })();
              }}
              open={true}
              vehicles={vehicles.map((v) => ({
                id: v.id,
                label: `${v.year} ${v.make} ${v.model}`,
              }))}
            />
          )}
          {viewMaintenance && (
            <MaintenanceViewModal
              maintenance={viewMaintenance}
              onClose={() => setViewMaintenance(null)}
              vehicles={vehicles}
            />
          )}
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Maintenance ({maintenance.length})
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                View:
              </span>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${maintenanceView === "grid" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() => setMaintenanceView("grid")}
              >
                Cards
              </button>
              <button
                className={`rounded px-2.5 py-1.5 text-xs transition-colors duration-200 sm:px-3 sm:text-sm ${maintenanceView === "table" ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                onClick={() => setMaintenanceView("table")}
              >
                Table
              </button>
              <button
                className="ml-2 flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:ml-4 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                onClick={() => setShowAddMaintenance(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Add Maintenance</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <select
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setMaintenanceStatusFilter(e.target.value)}
              value={maintenanceStatusFilter}
            >
              <option value="">All Status</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Completed">Completed</option>
            </select>
            <input
              className="w-full rounded border px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:flex-1 sm:text-sm"
              onChange={(e) => setMaintenanceSearch(e.target.value)}
              placeholder="Search maintenance..."
              type="text"
              value={maintenanceSearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 sm:text-sm"
              onClick={() => {
                const csv = [
                  ["Vehicle", "Status", "Date"],
                  ...filteredMaintenance.map((m) => [
                    m.vehicle,
                    m.status,
                    m.date,
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "maintenance.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {isLoading ? (
            maintenanceView === "table" ? (
              <div className="w-full overflow-x-auto">
                <table className="w-full rounded-lg bg-white text-xs shadow-md dark:bg-gray-800 sm:text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Vehicle
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                    {[...Array(5)].map((_, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4">
                          <div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-6 w-20 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    className="w-full rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                    key={i}
                  >
                    <div className="h-5 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="mt-2 h-4 w-28 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="mt-2 h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="mt-4 flex gap-2">
                      <div className="h-8 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                      <div className="h-8 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <>
              {maintenanceView === "table" ? (
                <div className="w-full overflow-x-auto">
                  <table className="w-full rounded-lg bg-white text-xs shadow-md dark:bg-gray-800 sm:text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Vehicle
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                      {filteredMaintenance.map((m) => (
                        <tr key={m.id}>
                          <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100">
                            {(() => {
                              const v = vehicles.find(
                                (veh) => veh.id === m.vehicle,
                              );
                              return v
                                ? `${v.brand} ${v.model} ${v.plate_number}`
                                : m.vehicle;
                            })()}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(m.status)}`}
                            >
                              {m.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                            {m.date}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                                onClick={() => setViewMaintenance(m)}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                                onClick={() => setEditMaintenance(m)}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                                onClick={() => setDeleteMaintenance(m)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredMaintenance.map((m) => (
                    <div
                      className="w-full rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                      key={m.id}
                    >
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        {(() => {
                          const v = vehicles.find(
                            (veh) => veh.id === m.vehicle,
                          );
                          return v
                            ? `${v.brand} ${v.model} ${v.plate_number}`
                            : m.vehicle;
                        })()}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Status: {m.status}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Date: {m.date}
                      </p>
                      <div className="mt-2 flex gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                          onClick={() => setViewMaintenance(m)}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                          onClick={() => setEditMaintenance(m)}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                          onClick={() => setDeleteMaintenance(m)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Delete Vehicle Confirmation Modal */}
      {deleteVehicle && (
        <div className="z-60 fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl dark:bg-gray-900 sm:p-6">
            <div className="mb-4 flex items-center">
              <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 sm:mr-4 sm:h-12 sm:w-12">
                <svg
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
                  Delete Vehicle
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <p className="mb-5 text-sm text-gray-700 dark:text-gray-300 sm:mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                {deleteVehicle.brand} {deleteVehicle.model}
              </span>{" "}
              ({deleteVehicle.plate_number})? This will permanently remove the
              vehicle and all associated data.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                className="rounded-lg bg-gray-300 px-4 py-2 font-medium text-gray-700 transition hover:bg-gray-400 disabled:opacity-50 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                disabled={isDeletingVehicle}
                onClick={() => setDeleteVehicle(null)}
              >
                Cancel
              </button>
              <button
                className="rounded-lg bg-red-600 px-4 py-2 font-medium text-white transition hover:bg-red-700 disabled:opacity-50 dark:bg-red-500 dark:hover:bg-red-600"
                disabled={isDeletingVehicle}
                onClick={() => handleDeleteVehicle(deleteVehicle)}
              >
                {isDeletingVehicle ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Driver Confirmation Modal */}
      {deleteDriver && (
        <div className="z-60 fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl dark:bg-gray-900 sm:p-6">
            <div className="mb-4 flex items-center">
              <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 sm:mr-4 sm:h-12 sm:w-12">
                <svg
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
                  Delete Driver
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <p className="mb-5 text-sm text-gray-700 dark:text-gray-300 sm:mb-6">
              Are you sure you want to delete driver
              <span className="font-semibold"> {deleteDriver.full_name} </span>?
              This will permanently remove the driver and all associated data.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                className="rounded-lg bg-gray-300 px-4 py-2 font-medium text-gray-700 transition hover:bg-gray-400 disabled:opacity-50 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                disabled={isDeletingDriver}
                onClick={() => setDeleteDriver(null)}
              >
                Cancel
              </button>
              <button
                className="rounded-lg bg-red-600 px-4 py-2 font-medium text-white transition hover:bg-red-700 disabled:opacity-50 dark:bg-red-500 dark:hover:bg-red-600"
                disabled={isDeletingDriver}
                onClick={() => handleDeleteDriver(deleteDriver)}
              >
                {isDeletingDriver ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Maintenance Confirmation Modal */}
      {deleteMaintenance && (
        <div className="z-60 fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl dark:bg-gray-900 sm:p-6">
            <div className="mb-4 flex items-center">
              <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 sm:mr-4 sm:h-12 sm:w-12">
                <svg
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
                  Delete Maintenance Record
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <p className="mb-5 text-sm text-gray-700 dark:text-gray-300 sm:mb-6">
              Are you sure you want to delete maintenance record for vehicle
              <span className="font-semibold">
                {" "}
                {deleteMaintenance.vehicle}{" "}
              </span>{" "}
              on <span className="font-semibold">{deleteMaintenance.date}</span>
              ? This will permanently remove the maintenance record and all
              associated data.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                className="rounded-lg bg-gray-300 px-4 py-2 font-medium text-gray-700 transition hover:bg-gray-400 disabled:opacity-50 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                disabled={isDeletingMaintenance}
                onClick={() => setDeleteMaintenance(null)}
              >
                Cancel
              </button>
              <button
                className="rounded-lg bg-red-600 px-4 py-2 font-medium text-white transition hover:bg-red-700 disabled:opacity-50 dark:bg-red-500 dark:hover:bg-red-600"
                disabled={isDeletingMaintenance}
                onClick={() => handleDeleteMaintenance(deleteMaintenance)}
              >
                {isDeletingMaintenance ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetManagement;
